import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Camera, Mail, Lock, ArrowLeft, Download, Smartphone, QrCode, X, Check, User, Phone, Building2, Copy, AlertCircle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useApp } from '../context/AppContext';
import { useToast } from '../hooks/use-toast';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { authAPI, publicAPI } from '../lib/api';
import { buildPixPayload } from '../lib/pix';
import { formatWhatsappLink, formatWhatsappDisplay } from '../lib/whatsapp';

export default function Auth({ initialMode }) {
  const [searchParams] = useSearchParams();
  const urlMode = searchParams.get('mode');
  const startMode = ['login', 'signup', 'pix'].includes(initialMode || urlMode) ? (initialMode || urlMode) : 'login';
  const [mode, setMode] = useState(startMode);
  const [form, setForm] = useState({ email: '', password: '' });
  const [signup, setSignup] = useState({ name: '', contactName: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [signupResult, setSignupResult] = useState(null); // { userId, plan }
  const [showInstall, setShowInstall] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [signupError, setSignupError] = useState('');
  const { loginUser, publicPlan } = useApp();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canInstall, isIOS, isStandalone, install } = usePWAInstall();
  const supportPhone = (publicPlan?.supportPhone || '').trim();
  const supportWaLink = supportPhone ? formatWhatsappLink(supportPhone, 'Olá! Preciso de ajuda com o AXSELL.') : '';
  const supportWaDisplay = supportPhone ? formatWhatsappDisplay(supportPhone) : '';

  useEffect(() => {
    const m = initialMode || urlMode;
    if (m && ['login', 'signup', 'pix'].includes(m)) setMode(m);
  }, [initialMode, urlMode]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (!form.email.trim() || !form.password) {
      setLoginError('Informe e-mail e senha.');
      return;
    }
    setBusy(true);
    try {
      const res = await loginUser({ email: form.email.trim().toLowerCase(), password: form.password });
      if (!res.ok) {
        const msg = res.message || '';
        if (/Network Error|timeout|ECONNAB/i.test(msg)) {
          setLoginError('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.');
        } else if (/Credenciais|401|inválidas|invalid/i.test(msg)) {
          setLoginError('E-mail ou senha incorretos.');
        } else {
          setLoginError(msg || 'Não foi possível entrar. Tente novamente.');
        }
        return;
      }
      toast({ title: 'Bem-vindo', description: res.user.name });
      navigate('/painel');
    } catch (err) {
      setLoginError('Erro inesperado. Tente novamente em instantes.');
    } finally {
      setBusy(false);
    }
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    setSignupError('');
    if (signup.password.length < 6) { setSignupError('Senha muito curta — use ao menos 6 caracteres.'); return; }
    if (signup.password !== signup.confirmPassword) { setSignupError('As senhas não coincidem.'); return; }
    setBusy(true);
    try {
      const res = await authAPI.signup({
        name: signup.name,
        contactName: signup.contactName,
        email: signup.email.toLowerCase().trim(),
        phone: signup.phone,
        password: signup.password,
      });
      setSignupResult(res);
      setMode('pix');
      toast({ title: 'Cadastro criado', description: 'Faça o pagamento via Pix para liberar sua conta.' });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || '';
      if (/Network Error|timeout/i.test(msg)) setSignupError('Sem conexão com o servidor. Tente novamente.');
      else setSignupError(msg || 'Não foi possível cadastrar. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  const handleInstall = async () => {
    if (canInstall) {
      const { installed } = await install();
      if (installed) toast({ title: 'App instalado!', description: 'Abra pela tela inicial.' });
    } else {
      setShowInstall(true);
    }
  };

  return (
    <div className="grain min-h-screen flex flex-col">
      <div className="max-w-6xl mx-auto w-full px-6 md:px-8 py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-[#a89e87] hover:text-[#e6dcc7] text-sm" data-testid="auth-back-link">
          <ArrowLeft size={16} /> Voltar
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-6">
            <span className="brand-mark mb-3"><Camera size={22} strokeWidth={2.2} /></span>
            <h1 className="font-serif text-3xl text-[#f2ecdf]">
              {mode === 'login' ? 'Entrar no painel' : mode === 'signup' ? 'Criar sua conta' : 'Pagamento via Pix'}
            </h1>
            <p className="text-sm text-[#a89e87] mt-2 text-center">
              {mode === 'login' && 'Acesse seus álbuns e vendas.'}
              {mode === 'signup' && 'Cadastre-se e comece hoje mesmo.'}
              {mode === 'pix' && 'Sua conta será liberada após a confirmação do pagamento link de pagamento https://pay.kiwify.com.br/BjFaekf.'}
            </p>
          </div>

          {/* Tabs Login / Cadastro */}
          {mode !== 'pix' && (
            <div className="grid grid-cols-2 gap-2 mb-4 card-glow p-1.5 rounded-xl">
              <button
                type="button"
                data-testid="tab-login"
                onClick={() => setMode('login')}
                className={`py-2 rounded-lg text-sm font-medium transition ${mode === 'login' ? 'bg-[#d6af6c] text-[#1a140a]' : 'text-[#a89e87] hover:text-[#f2ecdf]'}`}
              >Já tenho conta</button>
              <button
                type="button"
                data-testid="tab-signup"
                onClick={() => setMode('signup')}
                className={`py-2 rounded-lg text-sm font-medium transition ${mode === 'signup' ? 'bg-[#d6af6c] text-[#1a140a]' : 'text-[#a89e87] hover:text-[#f2ecdf]'}`}
              >Começar</button>
            </div>
          )}

          {mode === 'login' && (
            <form onSubmit={submitLogin} className="card-glow rounded-2xl p-6 md:p-8 space-y-4" noValidate>
              <Field icon={Mail} label="E-mail" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} placeholder="voce@estudio.com" testId="login-email" autoComplete="email" />
              <Field icon={Lock} label="Senha" type="password" value={form.password} onChange={v => setForm({ ...form, password: v })} placeholder="••••••" testId="login-password" autoComplete="current-password" />
              {loginError && (
                <div data-testid="login-error" className="flex items-start gap-2 px-3 py-2.5 rounded-md text-sm font-medium bg-red-500/10 text-red-300 border border-red-500/30">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>{loginError}</span>
                </div>
              )}
              <button type="submit" disabled={busy} data-testid="login-submit" className="btn-gold w-full py-3 rounded-md font-medium text-sm mt-2 disabled:opacity-60 disabled:cursor-not-allowed">
                {busy ? 'Entrando…' : 'Entrar'}
              </button>
              <p className="text-center text-xs text-[#7d735e] pt-2">
                Não tem conta?{' '}
                <button type="button" onClick={() => { setMode('signup'); setLoginError(''); }} className="gold-text hover:underline">Comece agora</button>
              </p>
            </form>
          )}

          {mode === 'signup' && (
            <form onSubmit={submitSignup} className="card-glow rounded-2xl p-6 md:p-8 space-y-3.5" data-testid="signup-form">
              <Field icon={Building2} label="Nome da empresa/fotógrafo" value={signup.name} onChange={v => setSignup({ ...signup, name: v })} placeholder="Studio Aurélio" testId="signup-company" />
              <Field icon={User} label="Nome do responsável" value={signup.contactName} onChange={v => setSignup({ ...signup, contactName: v })} placeholder="Aurélio Silva" testId="signup-contact" />
              <Field icon={Mail} label="E-mail" type="email" value={signup.email} onChange={v => setSignup({ ...signup, email: v })} placeholder="voce@estudio.com" testId="signup-email" />
              <Field icon={Phone} label="Telefone" value={signup.phone} onChange={v => setSignup({ ...signup, phone: v })} placeholder="(11) 99999-9999" testId="signup-phone" />
              <Field icon={Lock} label="Senha" type="password" value={signup.password} onChange={v => setSignup({ ...signup, password: v })} placeholder="mínimo 6 caracteres" testId="signup-password" />
              <Field icon={Lock} label="Confirmar senha" type="password" value={signup.confirmPassword} onChange={v => setSignup({ ...signup, confirmPassword: v })} placeholder="repita a senha" testId="signup-confirm" />
              {signupError && (
                <div data-testid="signup-error" className="flex items-start gap-2 px-3 py-2.5 rounded-md text-sm font-medium bg-red-500/10 text-red-300 border border-red-500/30">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>{signupError}</span>
                </div>
              )}
              <button type="submit" disabled={busy} data-testid="signup-submit" className="btn-gold w-full py-3 rounded-md font-medium text-sm mt-2 disabled:opacity-60 disabled:cursor-not-allowed">
                {busy ? 'Criando conta…' : 'finalizar cadastro'}
              </button>
            </form>
          )}

          {mode === 'pix' && signupResult && (
            <PixPayment result={signupResult} onBack={() => setMode('login')} />
          )}

          {mode === 'login' && !isStandalone && (
            <button
              onClick={handleInstall}
              className="w-full mt-4 card-glow rounded-2xl p-4 flex items-center gap-3 transition-all hover:border-[#d6af6c]/40 group"
              data-testid="install-app-btn"
            >
              <div className="w-11 h-11 rounded-lg bg-[#d6af6c] flex items-center justify-center flex-shrink-0">
                <Download size={20} strokeWidth={2.2} className="text-[#1a140a]" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm font-medium text-[#f2ecdf]">Baixar o aplicativo</div>
                <div className="text-xs text-[#a89e87]">Instalar no seu celular ou computador</div>
              </div>
              <div className="text-[#7d735e] group-hover:text-[#d6af6c] transition-colors">
                <Smartphone size={18} />
              </div>
            </button>
          )}

          <div className="text-center mt-6 space-y-2">
            <div className="pt-2 flex items-center justify-center gap-4">
              <Link to="/ajuda" className="text-xs text-[#a89e87] hover:text-[#d6af6c]">Central de Ajuda e suporte</Link>
              <span className="text-[#3a2f20]">•</span>
              <Link to="/admin" className="text-xs text-[#7d735e] hover:text-[#d6af6c]">Acesso administrativo</Link>
            </div> <a
  href="https://pay.kiwify.com.br/BjFaekf"
  target="_blank"
  rel="noopener noreferrer"
  className="text-xs text-[#7d735e] hover:text-[#d6af6c]"
>
  Comprar acesso
</a>
          </div>
        </div>
      </div>

      {showInstall && <InstallGuide isIOS={isIOS} onClose={() => setShowInstall(false)} />}
    </div>
  );
}

function PixPayment({ result, onBack }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const plan = result.plan || {};
  const [publicPlan, setPublicPlan] = useState(plan);

  // Refetch latest plan config in case admin changed anything between signup and now
  useEffect(() => {
    publicAPI.plan().then(setPublicPlan).catch(() => {});
  }, []);

  const p = publicPlan;
  const amount = Number(p.price || 47.90);
  const payload = p.pixKey
    ? buildPixPayload({ key: p.pixKey, amount, merchantName: (p.pixName || 'AXSELL').toUpperCase(), city: (p.pixCity || 'SAO PAULO').toUpperCase(), txid: `FL${(result.userId || 'X').slice(1, 9).toUpperCase()}` })
    : '';

  const copy = () => {
    if (!payload) return;
    navigator.clipboard.writeText(payload);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
    toast({ title: 'Código Pix copiado' });
  };

  return (
    <div className="card-glow rounded-2xl p-6 md:p-8 space-y-5" data-testid="pix-payment">
      <div className="text-center">
        <div className="text-xs uppercase tracking-wider text-[#a89e87] mb-1">Total a pagar</div>
        <div className="font-serif text-4xl gold-text">R$ {amount.toFixed(2).replace('.', ',')}</div>
        <div className="text-xs text-[#7d735e] mt-1">{p.planName || 'Plano Fotógrafo'} • {p.days || 30} dias de acesso</div>
      </div>

      {payload ? (
        <div className="bg-white rounded-xl p-5 flex flex-col items-center gap-2" data-testid="pix-qr">
          <QRCodeSVG value={payload} size={220} includeMargin={false} level="M" />
          <div className="text-[11px] text-[#1a140a] font-medium">Escaneie com o app do seu banco</div>
        </div>
      ) : (
        <div className="bg-amber-900/20 border border-amber-500/40 text-amber-200 text-sm rounded-lg p-4 text-center">
          Chave Pix não configurada. O administrador precisa cadastrá-la no painel.
        </div>
      )}

      {payload && (
        <>
          <div className="bg-[#12100b] border border-[#2a2318] rounded-md p-3">
            <div className="text-xs text-[#7d735e] mb-1">Pix Copia e Cola</div>
            <div className="text-[11px] text-[#e6dcc7] break-all font-mono max-h-24 overflow-y-auto">{payload}</div>
          </div>
          <button onClick={copy} data-testid="copy-pix-btn" className="btn-gold w-full py-3 rounded-md font-medium text-sm inline-flex items-center justify-center gap-2">
            {copied ? <><Check size={16} /> Código copiado</> : <><Copy size={16} /> Copiar código Pix</>}
          </button>
        </>
      )}

      <div className="bg-[#12100b] border border-[#2a2318] rounded-md p-4 text-sm text-[#a89e87] leading-relaxed">
        {p.supportPhone ? (
          <>Após pagar, envie o comprovante para o WhatsApp <a href={`https://wa.me/${String(p.supportPhone).replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-[#25D366]">{p.supportPhone}</a>. Sua conta será <b className="text-[#e6dcc7]">liberada em minutos</b> pelo administrador e você poderá acessar o painel.</>
        ) : (
          <>Após pagar, envie o comprovante ao administrador. Sua conta será <b className="text-[#e6dcc7]">liberada em minutos</b>.</>
        )}
      </div>

      <button onClick={onBack} className="w-full text-xs text-[#a89e87] hover:text-[#f2ecdf] pt-1" data-testid="pix-back">
        ← Voltar para a tela de login
      </button>
    </div>
  );
}

function InstallGuide({ isIOS, onClose }) {
  const [copied, setCopied] = useState(false);
  const url = window.location.origin;
  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="card-glow rounded-2xl p-6 md:p-8 max-w-md w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-2xl text-[#f2ecdf]">Baixar AXSELL</h3>
          <button onClick={onClose} className="text-[#7d735e] hover:text-[#f2ecdf]"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#a89e87] mb-5">Escaneie o QR Code com seu celular ou copie o link para instalar como aplicativo.</p>
        <div className="bg-white rounded-xl p-5 flex flex-col items-center gap-3 mb-4">
          <QRCodeSVG value={url} size={200} includeMargin={false} level="M" />
          <div className="flex items-center gap-1.5 text-xs text-[#1a140a] font-medium">
            <QrCode size={13} /> Aponte a câmera do celular
          </div>
        </div>
        <div className="bg-[#12100b] border border-[#2a2318] rounded-md p-3 mb-3">
          <div className="text-xs text-[#7d735e] mb-1">Link do aplicativo</div>
          <div className="text-[13px] text-[#e6dcc7] break-all font-mono">{url}</div>
        </div>
        <button onClick={copy} className="btn-gold w-full py-3 rounded-md font-medium text-sm inline-flex items-center justify-center gap-2 mb-4">
          {copied ? <><Check size={16} /> Link copiado</> : <><Download size={16} /> Copiar link</>}
        </button>
        <div className="border-t border-[#1c1710] pt-4">
          {isIOS ? (
            <>
              <div className="text-xs uppercase tracking-wider text-[#a89e87] mb-2">iPhone / iPad</div>
              <ol className="text-sm text-[#e6dcc7] space-y-2 list-decimal pl-5">
                <li>Abra o link acima no <b>Safari</b></li>
                <li>Toque no botão <b>Compartilhar</b> (ícone ↑)</li>
                <li>Toque em <b>Adicionar à Tela de Início</b></li>
                <li>Confirme em <b>Adicionar</b></li>
              </ol>
            </>
          ) : (
            <>
              <div className="text-xs uppercase tracking-wider text-[#a89e87] mb-2">Android / Chrome / Edge</div>
              <ol className="text-sm text-[#e6dcc7] space-y-2 list-decimal pl-5">
                <li>Abra o link no <b>Chrome</b></li>
                <li>Toque no menu <b>⋮</b> no topo</li>
                <li>Escolha <b>Instalar aplicativo</b></li>
                <li>Confirme e o ícone aparecerá</li>
              </ol>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, type = 'text', value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-[#a89e87] mb-1.5">{label}</span>
      <div className="relative">
        <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7d735e]" />
        <input type={type} required value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          data-testid={testId}
          className="w-full bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md pl-9 pr-3 py-2.5 text-sm text-[#f2ecdf] placeholder:text-[#5c5343] transition-colors" />
      </div>
    </label>
  );
}
